import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Loader2, ArrowLeft, FileText } from "lucide-react";
import { formatApiError } from "../lib/api";
import api from "../lib/api";
import { BasicInfoStep, DriverDocumentsStep } from "../components/RegisterFormSteps";
import WelcomePromoDialog from "../components/WelcomePromoDialog";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("passenger");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [welcomePromo, setWelcomePromo] = useState(null);
  const [error, setError] = useState("");

  // Driver specific
  const [cniNumber, setCniNumber] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [cniFront, setCniFront] = useState(null);
  const [cniBack, setCniBack] = useState(null);
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  // Optional referrer
  const [referrerName, setReferrerName] = useState("");
  // Legal — user must accept the Terms & Conditions before submitting the
  // FINAL step (step 1 for passenger, step 2 for driver).
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Optional referral code passed via ?ref=CODE (from a driver's QR code)
  const refCodeFromUrl = (searchParams.get("ref") || "").trim().toUpperCase();
  useEffect(() => {
    if (refCodeFromUrl) {
      try { sessionStorage.setItem("trigo_referral_code", refCodeFromUrl); } catch { /* noop */ }
    }
  }, [refCodeFromUrl]);

  const handleProfilePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { setError("Photo trop volumineuse (max 5MB)"); return; }
      setProfilePhoto(file);
      setProfilePreview(URL.createObjectURL(file));
      setError("");
    }
  };

  const uploadDocument = async (file, documentType, token) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", documentType);
    setUploadProgress(prev => ({...prev, [documentType]: "uploading"}));
    try {
      await api.post("/upload/driver-document", formData, {
        headers: { "Content-Type": "multipart/form-data", "Authorization": `Bearer ${token}` },
      });
      setUploadProgress(prev => ({...prev, [documentType]: "done"}));
    } catch (e) {
      setUploadProgress(prev => ({...prev, [documentType]: "error"}));
      throw e;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Le mot de passe doit contenir au moins 6 caracteres"); return; }

    if (role === "driver" && step === 1) { setStep(2); return; }

    // From here we are on the FINAL step — enforce Terms acceptance.
    if (!acceptedTerms) {
      setError("Vous devez accepter les termes et conditions pour créer votre compte.");
      return;
    }

    if (role === "driver") {
      if (!cniNumber) { setError("Numero de CNI requis"); return; }
      if (!licenseNumber) { setError("Numero de permis requis"); return; }
      if (!profilePhoto) { setError("Photo de profil obligatoire pour les chauffeurs"); return; }
      if (!cniFront || !cniBack) { setError("Images recto et verso de la CNI requises"); return; }
      if (!licenseFront || !licenseBack) { setError("Images recto et verso du permis requises"); return; }
    }

    setLoading(true);
    setError("");
    try {
      // Pull the pre-stored ref code (set from ?ref=CODE on first visit)
      // so we can both credit the referrer with 150 TFT at signup AND
      // link the attribution via the register endpoint.
      const savedRef = (() => { try { return sessionStorage.getItem("trigo_referral_code") || ""; } catch { return ""; } })();
      const data = await register(email, password, name, phone, role, cniNumber, licenseNumber, referrerName, acceptedTerms, savedRef);
      // Clear the stored code once it's been handed to the backend.
      if (savedRef) { try { sessionStorage.removeItem("trigo_referral_code"); } catch { /* noop */ } }

      if (role === "driver" && data.token) {
        try {
          if (profilePhoto) {
            const pfForm = new FormData();
            pfForm.append("file", profilePhoto);
            await api.post("/upload/profile-photo", pfForm, {
              headers: { "Content-Type": "multipart/form-data", "Authorization": `Bearer ${data.token}` },
            });
          }
          await uploadDocument(cniFront, "cni_front", data.token);
          await uploadDocument(cniBack, "cni_back", data.token);
          await uploadDocument(licenseFront, "license_front", data.token);
          await uploadDocument(licenseBack, "license_back", data.token);
        } catch (uploadErr) {
          if (process.env.NODE_ENV === "development") console.error("Upload error:", uploadErr);
        }
      }

      if (data.role === "driver") navigate("/driver", { replace: true });
      else {
        // Show welcome promo dialog first; navigation happens when user dismisses it
        if (data.welcome_promo) {
          setWelcomePromo({ ...data.welcome_promo });
        } else {
          navigate("/passenger", { replace: true });
        }
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Erreur lors de l'inscription");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen w-full bg-[#F5F7FA]"
      style={{ fontFamily: "Outfit, Inter, sans-serif" }}
      data-testid="register-page"
    >
      <div className="max-w-md mx-auto min-h-screen flex flex-col px-6 py-6">
        <Link
          to="/"
          className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-[#0A2540] hover:bg-[#0A2540] hover:text-white transition"
          data-testid="register-back"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>

        <div className="mt-8 mb-2 flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-[#0A2540] flex items-center justify-center overflow-hidden">
            <img src="/trigo-logo.png" alt="TrigoTour" className="w-12 h-12 object-contain" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#94A3B8]">
              Créez votre compte
            </p>
            <h1 className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
              {role === "driver" && step === 2 ? "Documents chauffeur" : "Inscription"}
            </h1>
          </div>
        </div>

        {role === "driver" && (
          <div className="flex items-center gap-2 my-4">
            <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? "bg-[#FACC15]" : "bg-[#E2E8F0]"}`} />
            <div className={`h-1.5 flex-1 rounded-full ${step >= 2 ? "bg-[#FACC15]" : "bg-[#E2E8F0]"}`} />
          </div>
        )}

        <div className="bg-white rounded-3xl border border-[#E2E8F0] p-5 sm:p-6 mt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {step === 1 && (
              <BasicInfoStep {...{ name, setName, email, setEmail, phone, setPhone, password, setPassword, role, setRole, showPw, setShowPw, referrerName, setReferrerName }} />
            )}

            {step === 2 && role === "driver" && (
              <DriverDocumentsStep {...{ cniNumber, setCniNumber, licenseNumber, setLicenseNumber, cniFront, setCniFront, cniBack, setCniBack, licenseFront, setLicenseFront, licenseBack, setLicenseBack, profilePhoto, profilePreview, referrerName, setReferrerName }} onProfilePhoto={handleProfilePhoto} setError={setError} />
            )}

            {error && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/20 text-[#EF4444] text-xs rounded-xl px-4 py-3" data-testid="register-error">
                {error}
              </div>
            )}

            {/* Terms & Conditions acceptance — shown ONLY on the final step
                (passengers submit on step 1, drivers submit on step 2). */}
            {(role !== "driver" || step === 2) && (
              <label
                className="flex items-start gap-3 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-3.5 cursor-pointer hover:border-[#FACC15] hover:bg-[#FEFCE8] transition"
                data-testid="register-terms-label"
              >
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => { setAcceptedTerms(e.target.checked); if (e.target.checked) setError(""); }}
                  className="mt-0.5 w-4 h-4 accent-[#FACC15] shrink-0 cursor-pointer"
                  data-testid="register-terms-checkbox"
                />
                <span className="text-[13px] leading-snug text-[#0A2540] flex-1">
                  <FileText className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-[#64748B]" />
                  J&apos;accepte les{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold underline text-[#0A2540] hover:text-[#FACC15] transition"
                    data-testid="register-terms-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    termes et conditions
                  </Link>{" "}
                  d&apos;utilisation de TrigoTour.
                </span>
              </label>
            )}

            <div className="flex gap-3 pt-2">
              {step === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 h-14 rounded-full border-[#E2E8F0]"
                  data-testid="register-back-btn"
                >
                  Retour
                </Button>
              )}
              <Button
                type="submit"
                disabled={loading || ((role !== "driver" || step === 2) && !acceptedTerms)}
                className={`flex-1 h-14 rounded-full font-bold text-base ${
                  step === 1 && role === "driver"
                    ? "bg-[#0A2540] text-white hover:bg-[#081b2e]"
                    : "bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] shadow-lg shadow-[#FACC15]/20"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="register-submit-btn"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {loading ? "Inscription..." : step === 1 && role === "driver" ? "Continuer" : "Créer mon compte"}
              </Button>
            </div>
          </form>
        </div>

        <p className="text-center text-sm text-[#64748B] mt-8 pb-4">
          Déjà un compte ?{" "}
          <Link to="/login" className="text-[#0A2540] font-bold hover:underline" data-testid="go-to-login">
            Se connecter
          </Link>
        </p>
      </div>
      <WelcomePromoDialog
        promo={welcomePromo}
        onClose={() => { setWelcomePromo(null); navigate("/passenger", { replace: true }); }}
      />
    </div>
  );
}
